import { useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { ChevronRight, Play } from "lucide-react";

export default function CTASection() {
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.3,
  });
  
  const phoneRef = useRef<HTMLDivElement>(null);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  return (
    <section id="cta" className="relative py-16 md:py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left column with text content */}
          <div className="lg:col-span-7" ref={ref}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6"
            >
              <motion.div variants={itemVariants} className="inline-block px-4 py-1 bg-expitrack-primary/10 rounded-full text-sm font-medium text-expitrack-primary mb-2">
                <span className="inline-block w-2 h-2 bg-expitrack-primary rounded-full mr-2 animate-pulse"></span>
                AI-Powered Expiry Tracking System
              </motion.div>
              
              <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Stop Wasting, <br />
                <span className="text-expitrack-primary">
                  Start Saving.
                </span>
              </motion.h2>
              
              <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl">
                Join thousands of conscious consumers and businesses who have reduced waste by up to 40% and saved hundreds on grocery bills.
              </motion.p>
              
              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 pt-4">
                <Link 
                  to="/auth" 
                  className="px-8 py-4 bg-expitrack-primary text-white rounded-full font-medium hover:bg-expitrack-primary/90 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  Start Free Trial <ChevronRight className="ml-2 w-5 h-5" />
                </Link>
                
                <button 
                  className="px-8 py-4 bg-white text-gray-700 rounded-full font-medium border border-gray-200 hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center justify-center"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
              
              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pt-2">
                <span className="flex items-center">
                  <svg className="mr-1.5 w-5 h-5 text-expitrack-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  No credit card required
                </span>
                <span className="flex items-center">
                  <svg className="mr-1.5 w-5 h-5 text-expitrack-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  14-day free trial
                </span>
                <span className="flex items-center">
                  <svg className="mr-1.5 w-5 h-5 text-expitrack-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  Cancel anytime
                </span>
              </motion.div>
            </motion.div>
          </div>
          
          {/* Right column with phone mockup */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              viewport={{ once: true }}
              className="relative"
              ref={phoneRef}
            >
              <div className="relative flex justify-center items-center">
                <div className="absolute inset-0 bg-gradient-to-r from-expitrack-primary/20 to-green-400/20 rounded-full blur-3xl opacity-30 transform scale-110"></div>
                <div className="relative w-64 md:w-72 h-[500px] md:h-[580px] bg-gray-900 rounded-[40px] overflow-hidden border-4 border-gray-800 shadow-2xl">
                  <div className="absolute inset-2 rounded-[32px] overflow-hidden bg-white">
                    <div className="w-full h-8 bg-gray-100 flex items-center justify-between px-6">
                      <div className="text-xs text-gray-800 font-medium">9:41</div>
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"></path>
                        </svg>
                        <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17 6h-2V5c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1v1H7c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-3 12h-4v-1h4v1zm2-3H8v-1h8v1zm0-3H8v-1h8v1z"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="flex flex-col h-full pt-4 px-4">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-br from-expitrack-primary to-expitrack-primary/80 rounded-lg mr-2 flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                          </div>
                          <div className="font-bold text-gray-800">ExpiTrack</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0018 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-100 rounded-2xl p-4 mb-4">
                        <div className="text-xs text-gray-500 mb-2">Your Impact</div>
                        <div className="flex justify-between items-end">
                          <div>
                            <div className="text-2xl font-bold text-gray-800">$247</div>
                            <div className="text-xs text-gray-500">Saved this month</div>
                          </div>
                          <svg width="100" height="40" viewBox="0 0 100 40" fill="none" stroke="currentColor" className="text-expitrack-primary">
                            <path d="M0 30L5 28L10 32L15 25L20 28L25 20L30 22L35 18L40 15L45 20L50 15L55 18L60 12L65 15L70 8L75 10L80 5L85 8L90 2L95 5L100 10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      </div>
                      <div className="bg-gray-100 rounded-2xl p-4 mb-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="text-xs text-gray-500">Expiring Soon</div>
                          <div className="text-xs text-expitrack-primary">View All</div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-white p-2 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-expitrack-alert/20 rounded-md flex items-center justify-center mr-2">
                                <svg className="w-5 h-5 text-expitrack-alert" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-800">Milk</div>
                                <div className="text-[10px] text-expitrack-alert">Expires Tomorrow</div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between bg-white p-2 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-yellow-500/20 rounded-md flex items-center justify-center mr-2">
                                <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
                                </svg>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-800">Yogurt</div>
                                <div className="text-[10px] text-yellow-500">Expires in 3 days</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-center mt-4">
                        <motion.div 
                          className="w-16 h-16 bg-gradient-to-r from-expitrack-primary to-green-400 rounded-full flex items-center justify-center shadow-lg shadow-expitrack-primary/30"
                          animate={{ y: [0, -8, 0] }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "loop",
                            ease: "easeInOut"
                          }}
                        >
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                          </svg>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
